import { type BucketAggregate, MAX_GAPFILL_BUCKETS } from "verdi-common/schemas/time-series/data-stream-query";
import { listDataStreamPoints } from "@/api/orval/generated/data-stream";
import { BUCKET_WIDTH_MS, type BucketWidth, type Resolution } from "../resolution/pick-resolution";
import type { StreamPoint } from "../stream-cache/stream-cache-store";
import type { FetchedStream } from "./types";

const PAGE_LIMIT = 5000;

/**
 * Runaway guard on the pagination loop. The cursor-advance check below already guarantees
 * termination, so this only trips if the API starts behaving pathologically — at PAGE_LIMIT per
 * page it sits far above any window a chart can plot.
 */
const MAX_PAGES = 1000;

/** One point from the non-bucketed response shape. */
type RawPoint = { occurredAt: Date; convertedValue: number };

/** The aggregate a stream with no `preferredBucketAggregate` is read with. */
const DEFAULT_AGGREGATE: BucketAggregate = "mean";

/** Fill min/max/count from a single value — neither response shape carries a real spread. */
const singleValueBucket = (occurredAt: Date, value: number | null): StreamPoint => ({
    // Epoch-ms from here on: mergePoints keys a Map<number>, so a Date can't dedup by value.
    t: new Date(occurredAt).getTime(),
    avg: value,
    min: value,
    max: value,
    count: value === null ? 0 : 1,
});

export async function fetchSingleStreamData(
    streamId: number,
    from: Date | string | number,
    to: Date | string | number,
    resolution: Resolution,
    aggregate: BucketAggregate = DEFAULT_AGGREGATE,
): Promise<FetchedStream> {
    const windowStart = new Date(from);
    const windowEnd = new Date(to);

    return resolution === "raw"
        ? fetchRawStreamData(streamId, windowStart, windowEnd)
        : fetchBucketedStreamData(streamId, windowStart, windowEnd, resolution, aggregate);
}

async function fetchRawStreamData(streamId: number, windowStart: Date, windowEnd: Date): Promise<FetchedStream> {
    const allPoints: RawPoint[] = [];
    /** Newest `occurredAt` seen across all pages — the coverage watermark for the caller. */
    let lastOccurredAt: Date | undefined;
    /** Exclusive lower bound for the next page; undefined until the first page has returned. */
    let pageStart: Date | undefined;

    for (let pageIndex = 0; pageIndex < MAX_PAGES; pageIndex += 1) {
        const page = await listDataStreamPoints(streamId, {
            "timeRange[start]": pageStart ?? windowStart,
            "timeRange[end]": windowEnd,
            // The initial graph window includes both selected endpoints. Subsequent
            // pages move only the lower edge to the cursor, exclusively, to avoid
            // returning the last point of the prior page again.
            "timeRange[startInclusive]": pageStart === undefined,
            "timeRange[endInclusive]": true,
            limit: PAGE_LIMIT,
        });

        // This endpoint returns a union of the raw and bucketed shapes, discriminated by `bucketing`.
        // This path sends no `bucketWidth`, so only the raw branch is reachable; checking it
        // narrows the union and would surface an API contract change rather than mistyping it.
        if (page.bucketing !== null) {
            throw new Error(`Unexpected bucketed response for stream ${streamId}`);
        }

        // `cursor` is the occurredAt of the last delivered point, or null for an empty page.
        const { points, cursor } = page;

        if (!Array.isArray(points)) {
            throw new Error(`Malformed points response for stream ${streamId}`);
        }

        allPoints.push(...points);
        if (cursor !== null) lastOccurredAt = cursor;

        // A short page means the window is drained. This trusts the array length rather than the
        // reported `count`, so the two disagreeing can't keep the loop spinning.
        if (points.length < PAGE_LIMIT) break;

        // Only a cursor that strictly advances narrows the window. Without this, an absent or
        // repeated cursor would re-send a byte-identical request forever.
        if (cursor === null || (pageStart !== undefined && cursor.getTime() <= pageStart.getTime())) break;

        // The next page would use `cursor` as an *exclusive* lower bound, so once it reaches
        // `windowEnd` the requested range contains no instants — which the API rejects.
        if (cursor.getTime() >= windowEnd.getTime()) break;

        pageStart = cursor;
    }

    // At this tier the chart is zoomed in far enough to plot every reading, so each point is
    // its own bucket.
    const points = allPoints.map((item) => singleValueBucket(item.occurredAt, item.convertedValue));

    return { points, lastOccurredAt };
}

/**
 * Fetch server-aggregated buckets, one value per bucket under `aggregate`.
 *
 * Bounds are widened out to bucket boundaries before asking. The server labels buckets on its
 * own fixed grid but averages only the points inside the bounds you send, so an unaligned
 * request comes back with whole-looking buckets holding partial means — and since the cache
 * merges on bucket start, a later fetch overwrites one partial with another.
 *
 * Empty buckets arrive as nulls and are kept — `prepareSeries` decides where a gap breaks the line.
 */
async function fetchBucketedStreamData(
    streamId: number,
    windowStart: Date,
    windowEnd: Date,
    resolution: BucketWidth,
    aggregate: BucketAggregate,
): Promise<FetchedStream> {
    const widthMs = BUCKET_WIDTH_MS[resolution];

    // The server's bucket origin (2000-01-03T00:00Z) is a whole multiple of every width we ask
    // for, so flooring plain epoch-ms lands on its grid. Pinned in the test file.
    const alignedStart = Math.floor(windowStart.getTime() / widthMs) * widthMs;
    const alignedEnd = Math.ceil(windowEnd.getTime() / widthMs) * widthMs;

    const chunkMs = MAX_GAPFILL_BUCKETS * widthMs;
    const points: StreamPoint[] = [];
    /** Start of the last bucket that had a value — the coverage watermark for the caller. */
    let lastOccurredAt: Date | undefined;

    for (let chunkStart = alignedStart; chunkStart < alignedEnd; chunkStart += chunkMs) {
        const chunkEnd = Math.min(chunkStart + chunkMs, alignedEnd);

        const page = await listDataStreamPoints(streamId, {
            "timeRange[start]": new Date(chunkStart),
            "timeRange[end]": new Date(chunkEnd),
            // Half-open, so abutting chunks don't both claim the boundary bucket.
            "timeRange[startInclusive]": true,
            "timeRange[endInclusive]": false,
            bucketWidth: resolution,
            aggregate,
        });

        // Mirror of the raw path's check: a null `bucketing` here means the API ignored the
        // bucketWidth we sent, and these values would not be the aggregates we asked for.
        if (page.bucketing === null) {
            throw new Error(`Unexpected raw response for stream ${streamId}`);
        }

        const { points: buckets } = page;

        if (!Array.isArray(buckets)) {
            throw new Error(`Malformed points response for stream ${streamId}`);
        }

        points.push(...buckets.map((item) => singleValueBucket(item.occurredAt, item.convertedValue)));

        // A widened leading bucket starts before the caller's range; watermarking it inverts coverage.
        // Empty trailing buckets are skipped too: covering them means a late reading never gets re-fetched.
        const lastFilledInRange = buckets
            .filter(
                (item) => item.convertedValue !== null && new Date(item.occurredAt).getTime() >= windowStart.getTime(),
            )
            .at(-1);
        if (lastFilledInRange) lastOccurredAt = new Date(lastFilledInRange.occurredAt);
    }

    return { points, lastOccurredAt };
}
